import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Practitioner } from "@medplum/fhirtypes";
import { useMedplum, useMedplumProfile } from "@medplum/react";

import { ComboboxItem, MultiSelect } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";

import { setFormFieldValue } from "@/intake-forms/utils/intake-forms";
import { fetchActiveCareTeamPractitionersForPatient } from "@/shared/services/care-team-search";
import { toast } from "@/utils/notifications";
import { getName } from "@/utils/str";

interface LaborFlowsheetSignatureInitialsFieldProps<T> {
  label?: string;
  form: UseFormReturnType<T>;
  name: keyof T & string;
  clientId: string;
  placeholder?: string;
  dataTestId?: string;
  /** When false, empty signature stays empty (edit mode). Defaults to true for quick-add. */
  autoPopulateSignature?: boolean;
}

type SignatureSelectOption = ComboboxItem;

export function LaborFlowsheetSignatureInitialsField<T>({
  label,
  form,
  name,
  clientId,
  placeholder,
  dataTestId,
  autoPopulateSignature = true,
}: LaborFlowsheetSignatureInitialsFieldProps<T>) {
  const medplum = useMedplum();
  const currentUser = useMedplumProfile();
  const [fetchedCareTeamOptions, setFetchedCareTeamOptions] = useState<SignatureSelectOption[]>([]);
  const isInitialLoad = useRef(true);
  const hasUserInteracted = useRef(false);

  // Declared (and so, on mount, run) before the pre-select effect below, seeing this render's
  // pre-update `form.values` - a no-op on the very first commit regardless, since `isInitialLoad`
  // already defaults to `true`. On a later reopen with a cleared field, this is what lets
  // pre-select run again; don't rely on it alone to gate whether pre-select should still run, since
  // the pre-select effect's own guard re-checks the real form value too.
  useEffect(() => {
    const currentValue = form.values[name];
    if (!currentValue || (Array.isArray(currentValue) && currentValue.length === 0)) {
      isInitialLoad.current = true;
    }
  }, [form.values, name]);

  // Always includes the signed-in practitioner, however the fetched list is currently populated -
  // a pure derivation, so it can't go stale or drop them regardless of fetch timing or ordering.
  const careTeamOptions = useMemo(() => {
    if (currentUser?.resourceType !== "Practitioner") {
      return fetchedCareTeamOptions;
    }
    const practitioner = currentUser as Practitioner;
    const { fullName, initials } = getName(practitioner);
    const currentUserOption = {
      value: practitioner.id as string,
      label: `${initials} (${fullName})`,
    };
    if (fetchedCareTeamOptions.some((option) => option.value === currentUserOption.value)) {
      return fetchedCareTeamOptions;
    }
    return [...fetchedCareTeamOptions, currentUserOption];
  }, [fetchedCareTeamOptions, currentUser]);

  // Pre-selects the signed-in practitioner as soon as `currentUser` resolves, independent of the
  // care team search below - `useMedplumProfile` already knows who's signed in without a network
  // call, so a field that's required doesn't have to wait on an unrelated fetch to be satisfied.
  // Only sets the form value; `careTeamOptions` above always includes the current user regardless
  // of whether (or when) this runs, so there's nothing to race here.
  useEffect(() => {
    if (!autoPopulateSignature) {
      return;
    }
    if (currentUser?.resourceType !== "Practitioner") {
      return;
    }
    if (!isInitialLoad.current || hasUserInteracted.current) {
      return;
    }
    const currentValue = form.values[name];
    if (currentValue && (!Array.isArray(currentValue) || currentValue.length > 0)) {
      return;
    }
    const practitioner = currentUser as Practitioner;
    setFormFieldValue(form, name, [practitioner.id]);
    isInitialLoad.current = false;
  }, [autoPopulateSignature, currentUser, form, name]);

  useEffect(() => {
    let isCancelled = false;

    const loadCareTeamOptions = async (patientId: string) => {
      try {
        const practitioners = await fetchActiveCareTeamPractitionersForPatient(medplum, patientId);

        const includedPractitioners = practitioners.map((practitioner) => {
          const { fullName, initials } = getName(practitioner);
          return {
            value: practitioner.id as string,
            label: `${initials} (${fullName})`,
          };
        });

        if (isCancelled) {
          return;
        }
        setFetchedCareTeamOptions(includedPractitioners);
      } catch (error) {
        if (isCancelled) {
          return;
        }
        console.error("Error fetching care team:", error);
        setFetchedCareTeamOptions([]);
        toast({
          type: "warning",
          title: "Could not load care team signatures",
          message: "Signature options may be incomplete. Try closing and reopening the form.",
          autoClose: false,
        });
      }
    };

    if (clientId) {
      void loadCareTeamOptions(clientId);
    } else {
      setFetchedCareTeamOptions([]);
    }

    return () => {
      isCancelled = true;
    };
  }, [clientId, medplum]);

  const handleChange = useCallback(
    (selectedValues: string[]) => {
      setFormFieldValue(form, name, selectedValues);
      hasUserInteracted.current = true;
    },
    [form, name],
  );

  const value = form.values[name];
  const multiSelectValue = Array.isArray(value) ? value : [];

  return (
    <MultiSelect
      clearable
      searchable
      name={name}
      withAsterisk
      label={label}
      data={careTeamOptions}
      onChange={handleChange}
      maxDropdownHeight={400}
      value={multiSelectValue}
      data-testid={dataTestId ?? name}
      nothingFoundMessage="No signatures found"
      error={form.errors[name] as string | undefined}
      placeholder={multiSelectValue.length === 0 ? (placeholder ?? "Select signatures") : ""}
    />
  );
}
